'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import { 
    Search, 
    ChevronRight,
    Loader2,
    Layers,
    Server as ServerIcon
} from 'lucide-react';
import AdDisplay from "@/app/components/AdDisplay";

interface ServerStatusData {
    online: boolean;
    ip: string;
    port: number;
    hostname: string;
    version?: string;
    players: {
        online: number;
        max: number;
    };
    motd: {
        raw: string[];
        clean: string[];
        html: string[];
    };
    icon?: string | null;
    ping?: number;
}

export default function ServerStatusClient() {
    const searchParams = useSearchParams();
    const router = useRouter();

    const [addressInput, setAddressInput] = useState('');
    const [edition, setEdition] = useState<'java' | 'bedrock'>('java');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [serverData, setServerData] = useState<ServerStatusData | null>(null);

    const fetchStatus = useCallback(async (targetAddress: string, targetType: 'java' | 'bedrock') => {
        if (!targetAddress.trim()) return;

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`/api/server-status?address=${encodeURIComponent(targetAddress.trim())}&type=${targetType}`);
            if (!res.ok) {
                const errData = (await res.json().catch(() => null)) as any;
                throw new Error(errData?.error || 'Failed to ping Minecraft server.');
            }
            const data: ServerStatusData = (await res.json()) as any;
            setServerData(data);
        } catch (err: any) {
            setError(err.message || 'Could not connect to server.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Sync from URL search params on mount
    useEffect(() => {
        const ipFromUrl = searchParams.get('ip') || searchParams.get('address');
        const typeFromUrl = searchParams.get('type');
        if (ipFromUrl) {
            setAddressInput(ipFromUrl);
            const resolvedType = typeFromUrl === 'bedrock' ? 'bedrock' : 'java';
            setEdition(resolvedType);
            fetchStatus(ipFromUrl, resolvedType);
        } else {
            // Default demo server: Hypixel
            setAddressInput('play.hypixel.net');
            fetchStatus('play.hypixel.net', 'java');
        }
    }, [searchParams, fetchStatus]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!addressInput.trim()) return;
        router.push(`/server-status?ip=${encodeURIComponent(addressInput.trim())}&type=${edition}`, { scroll: false });
        fetchStatus(addressInput, edition);
    };

    return (
        <div className="bg-slate-50/50 text-slate-900 min-h-screen">
            {/* Embedded custom Minecraft font */}
            <style jsx global>{`
                @font-face {
                    font-family: 'Minecraftia';
                    src: url('/fonts/Minecraftia.ttf') format('truetype');
                    font-weight: normal;
                    font-style: normal;
                    font-display: block;
                }
                .mc-font {
                    font-family: 'Minecraftia', monospace;
                }
                .mc-shadow {
                    text-shadow: 2px 2px 0px rgba(0, 0, 0, 0.9);
                }
            `}</style>

            <div className="max-w-4xl mx-auto px-4 py-8 md:py-12 space-y-7">
                
                {/* BREADCRUMB & HEADER */}
                <header className="space-y-4">
                    <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                        <Link href="/" className="hover:text-emerald-600 transition-colors">Home</Link>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-400">Tools</span>
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-800 font-semibold">Server Status</span>
                    </nav>

                    <div>
                        <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                            Minecraft Server <span className="text-emerald-600">Status Checker</span>
                        </h1>
                        <p className="text-slate-600 text-sm md:text-base mt-2 max-w-xl font-medium leading-relaxed">
                            Enter any Minecraft server IP to check online status, player count, and live server banner.
                        </p>
                    </div>
                </header>

                {/* AD SLOT 1: TOP HORIZONTAL */}
                <div className="w-full">
                    <AdDisplay slot="3287602375" adLayout="horizontal" />
                </div>

                {/* SEARCH INPUT WORKSPACE */}
                <div className="bg-white border border-slate-200/80 rounded-2xl p-5 md:p-6 shadow-xs space-y-3.5">
                    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 items-stretch">
                        
                        {/* Server IP Input */}
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                <ServerIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                value={addressInput}
                                onChange={(e) => setAddressInput(e.target.value)}
                                placeholder="Enter server IP (e.g. play.hypixel.net or mc.server.net:25565)"
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:outline-hidden transition-all shadow-2xs"
                            />
                        </div>

                        {/* Edition Selector */}
                        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200/80 shrink-0 self-center sm:self-auto">
                            <button
                                type="button"
                                onClick={() => setEdition('java')}
                                className={`px-3 py-2 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
                                    edition === 'java' 
                                        ? 'bg-white text-slate-900 shadow-2xs' 
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                Java
                            </button>
                            <button
                                type="button"
                                onClick={() => setEdition('bedrock')}
                                className={`px-3 py-2 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer ${
                                    edition === 'bedrock' 
                                        ? 'bg-white text-slate-900 shadow-2xs' 
                                        : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                Bedrock
                            </button>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading || !addressInput.trim()}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-all shadow-xs active:scale-95 cursor-pointer shrink-0"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Pinging...</span>
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4" />
                                    <span>Check Status</span>
                                </>
                            )}
                        </button>
                    </form>

                </div>

                {/* ERROR ALERT */}
                {error && (
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-800 text-xs font-mono">
                        <span className="font-bold">Error:</span> {error}
                    </div>
                )}

                {/* SERVER BANNER DISPLAY */}
                {serverData && (
                    <div className="space-y-6">
                        
                        {/* Server List Banner Entry Card */}
                        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 md:p-6 shadow-xs space-y-3">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <div className={`w-2.5 h-2.5 rounded-full ${serverData.online ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                                    <span className="text-xs font-mono uppercase tracking-wider text-slate-800 font-bold">
                                        Server Banner
                                    </span>
                                </div>
                                <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-md ${
                                    serverData.online 
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60' 
                                        : 'bg-rose-50 text-rose-700 border border-rose-200/60'
                                }`}>
                                    {serverData.online ? 'ONLINE' : 'OFFLINE'}
                                </span>
                            </div>

                            {/* In-Game Minecraft Dark Browser HUD Entry */}
                            <div className="bg-[#12161f] border border-slate-800 rounded-xl p-4 font-mono select-none text-white shadow-inner">
                                <div className="flex items-start gap-4">
                                    
                                    {/* 64x64 Icon Box */}
                                    <div className="w-16 h-16 bg-black/60 border border-white/10 rounded overflow-hidden flex items-center justify-center shrink-0">
                                        {serverData.icon ? (
                                            <img
                                                src={serverData.icon}
                                                alt={`${serverData.hostname} icon`}
                                                className="w-full h-full object-contain image-rendering-pixelated"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex flex-col items-center justify-center text-slate-400">
                                                <Layers className="w-5 h-5 text-emerald-400 mb-0.5" />
                                                <span className="text-[9px] font-mono font-bold text-slate-400">MC</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Server Info & Rendered Live MOTD */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <span className="mc-font text-sm text-white truncate mc-shadow font-bold">
                                                {serverData.hostname}
                                            </span>
                                            
                                            <div className="flex items-center gap-2.5 shrink-0">
                                                {serverData.online ? (
                                                    <span className="mc-font text-xs text-slate-400 mc-shadow">
                                                        <span className="text-emerald-400 font-bold">{serverData.players.online.toLocaleString()}</span>
                                                        <span className="text-slate-600">/</span>
                                                        <span>{serverData.players.max.toLocaleString()}</span>
                                                    </span>
                                                ) : (
                                                    <span className="mc-font text-xs text-rose-500 mc-shadow font-bold">
                                                        Offline
                                                    </span>
                                                )}

                                                {/* Ping Bars */}
                                                <div 
                                                    className="flex items-end gap-0.5"
                                                    title={`Latency: ${serverData.ping || 0}ms`}
                                                >
                                                    <span className={`w-1 h-1.5 rounded-2xs ${serverData.online ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                    <span className={`w-1 h-2.5 rounded-2xs ${serverData.online ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                                                    <span className={`w-1 h-3.5 rounded-2xs ${serverData.online && (serverData.ping ?? 999) < 250 ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                                                    <span className={`w-1 h-4 rounded-2xs ${serverData.online && (serverData.ping ?? 999) < 150 ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                                                    <span className={`w-1 h-5 rounded-2xs ${serverData.online && (serverData.ping ?? 999) < 60 ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Rendered Live MOTD Lines */}
                                        <div className="space-y-1 text-xs mc-font leading-relaxed">
                                            {serverData.motd.html && serverData.motd.html.length > 0 ? (
                                                serverData.motd.html.map((lineHtml, i) => (
                                                    <div 
                                                        key={i} 
                                                        className="min-h-[18px] break-all whitespace-pre-wrap mc-shadow"
                                                        dangerouslySetInnerHTML={{ __html: lineHtml }}
                                                    />
                                                ))
                                            ) : (
                                                <div className="min-h-[18px] text-slate-400">
                                                    {serverData.online ? 'A Minecraft Server' : 'Could not resolve host'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* AD SLOT 2: MID CONTENT */}
                        <div className="w-full">
                            <AdDisplay slot="7849102938" adLayout="horizontal" />
                        </div>

                    </div>
                )}

                {/* AD SLOT 3: BOTTOM HORIZONTAL */}
                <div className="w-full pt-2">
                    <AdDisplay slot="4920192847" adLayout="horizontal" />
                </div>

            </div>
        </div>
    );
}
